import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../lib/apiCache';

const DEFAULT_SLIDES = [
  {
    id: '1',
    title: 'Conecte-se com a Ásia',
    subtitle: 'Missões Comerciais 2026',
    description: 'Participe das nossas missões comerciais para China, Japão e Coreia do Sul.',
    media_url: 'https://images.unsplash.com/photo-1547981609-4b6bfe67ca0b?w=1200&q=70&auto=format&fit=crop',
    cta_text: 'Ver Missões',
    cta_link: '/eventos',
    overlay_opacity: 60,
    text_alignment: 'center',
  },
  {
    id: '2',
    title: 'Canton Fair & Grandes Feiras da China',
    subtitle: 'Feiras & Exposições Internacionais',
    description: 'Levamos você às maiores feiras de negócios do mundo. Sourcing direto, contato com fabricantes e oportunidades exclusivas.',
    media_url: 'https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?auto=format&fit=crop&w=1200&q=70',
    cta_text: 'Saiba Mais',
    cta_link: '/eventos',
    overlay_opacity: 58,
    text_alignment: 'left',
  },
  {
    id: '3',
    title: 'Xangai — Coração Financeiro da Ásia',
    subtitle: 'Metrópoles Asiáticas',
    description: 'Onde os negócios acontecem. Conectamos você ao epicentro financeiro e comercial da Ásia com suporte completo.',
    media_url: 'https://images.unsplash.com/photo-1538428494232-9c0d8a3ab403?w=1200&q=70&auto=format&fit=crop',
    cta_text: 'Conhecer Mercados',
    cta_link: '/servicos',
    overlay_opacity: 50,
    text_alignment: 'center',
  },
  {
    id: '4',
    title: 'Sourcing Industrial na China',
    subtitle: 'Pesquisa de Produtos & Fábricas',
    description: 'Visitamos fábricas, validamos fornecedores e fazemos a ponte entre o Brasil e a indústria asiática de ponta.',
    media_url: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&w=1200&q=70',
    cta_text: 'Ver Serviços',
    cta_link: '/servicos',
    overlay_opacity: 62,
    text_alignment: 'left',
  },
  {
    id: '5',
    title: 'Seja um Associado BACC',
    subtitle: 'Brasil Asia Chamber of Commerce',
    description: 'Conectando Brasil e Ásia através de oportunidades reais de negócios, diplomacia econômica e parcerias de alto impacto.',
    media_url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&q=70&auto=format&fit=crop',
    cta_text: 'Associe-se',
    cta_link: '/associacao',
    overlay_opacity: 65,
    text_alignment: 'center',
  },
];

const HeroSlider = () => {
  const [slides, setSlides] = useState(DEFAULT_SLIDES);
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  const resetTimer = (count) => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % count);
    }, 6000);
  };

  useEffect(() => {
    resetTimer(slides.length);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preload only the next slide image
  useEffect(() => {
    const next = (current + 1) % slides.length;
    const nextUrl = slides[next]?.media_url;
    if (nextUrl) {
      const img = new window.Image();
      img.src = nextUrl;
    }
  }, [current, slides]);

  // Fetch slides from API and replace defaults if available
  useEffect(() => {
    api.get('/hero-slides', { language: 'pt' })
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          setSlides(data);
          resetTimer(data.length);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTo = (idx) => { setCurrent(idx); resetTimer(slides.length); };
  const prev = () => goTo((current - 1 + slides.length) % slides.length);
  const next = () => goTo((current + 1) % slides.length);

  const slide = slides[current] || slides[0];
  const alignClass = slide.text_alignment === 'left'
    ? 'text-left items-start'
    : slide.text_alignment === 'right'
    ? 'text-right items-end'
    : 'text-center items-center';

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#1A1F4B]"
    >
      {slides.map((s, idx) => (
        <div
          key={s.id}
          className="absolute inset-0 transition-opacity duration-700 ease-in-out"
          style={{ opacity: idx === current ? 1 : 0 }}
          aria-hidden={idx !== current}
        >
          <img
            src={s.media_url}
            alt=""
            fetchPriority={idx === 0 ? 'high' : 'low'}
            decoding={idx === 0 ? 'sync' : 'async'}
            loading={idx === 0 ? 'eager' : 'lazy'}
            className="w-full h-full object-cover"
          />
          <div
            className="absolute inset-0 bg-[#1A1F4B]"
            style={{ opacity: (s.overlay_opacity ?? 65) / 100 }}
          />
        </div>
      ))}

      <div className={`relative z-10 max-w-5xl mx-auto px-6 pt-20 flex flex-col ${alignClass}`}>
        {slide.subtitle && (
          <div
            key={`sub-${current}`}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-6 animate-fade-in"
          >
            <span className="w-2 h-2 bg-[#C9A84C] rounded-full" />
            <span className="text-white/90 text-sm font-medium">{slide.subtitle}</span>
          </div>
        )}

        <h1
          key={`title-${current}`}
          className="font-heading text-4xl md:text-6xl lg:text-7xl text-white mb-6 animate-fade-in"
        >
          {slide.title}
        </h1>

        {slide.description && (
          <p
            key={`desc-${current}`}
            className="text-white/80 text-lg md:text-xl max-w-2xl mb-10 animate-fade-in"
          >
            {slide.description}
          </p>
        )}

        {slide.cta_text && slide.cta_link && (
          <Link
            key={`cta-${current}`}
            to={slide.cta_link}
            className="bg-[#C9A84C] text-[#1A1F4B] px-10 py-4 font-bold text-base hover:bg-[#D4B663] transition-colors inline-block animate-fade-in shadow-lg"
          >
            {slide.cta_text}
          </Link>
        )}
      </div>

      <button
        onClick={prev}
        aria-label="Slide anterior"
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/10 backdrop-blur-sm text-white hover:bg-[#C9A84C] hover:text-[#1A1F4B] rounded-full transition-all"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>
      <button
        onClick={next}
        aria-label="Próximo slide"
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/10 backdrop-blur-sm text-white hover:bg-[#C9A84C] hover:text-[#1A1F4B] rounded-full transition-all"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-3 items-center">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => goTo(idx)}
            aria-label={`Slide ${idx + 1}`}
            className={`transition-all duration-300 rounded-full ${
              idx === current
                ? 'w-10 h-2.5 bg-[#C9A84C]'
                : 'w-2.5 h-2.5 bg-white/40 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
